import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { setStep, setUserCredentials } from "../../store/reducers/authSlice";
import { useSendOtpMutation } from "../../api/authApi";
import { toast } from "react-toastify";
import { InfoOutlined } from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import EiLogo from "../../Icons/EiLogo.svg";
import ICTLogo from "../../Icons/LOGOICT.png";

const LoginScreen = () => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    emailId: "",
    employeeId: "",
  });
  const [errors, setErrors] = useState({});
  const [sendOtp, { isLoading }] = useSendOtpMutation();

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateEmployeeId = (id) => {
    return id && id.trim().length >= 3;
  };

  const handleInputChange = (field, value) => {
    setErrors({});
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.emailId.trim()) {
      newErrors.emailId = "Email ID is required";
    } else if (!validateEmail(formData.emailId)) {
      newErrors.emailId = "Please enter a valid email address";
    }

    if (!formData.employeeId.trim()) {
      newErrors.employeeId = "Employee ID is required";
    } else if (!validateEmployeeId(formData.employeeId)) {
      newErrors.employeeId = "Employee ID must be at least 3 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogin = async () => {
    if (!validateForm()) return;

    try {
      dispatch(setUserCredentials(formData));
      await sendOtp(formData).unwrap();
      dispatch(setStep("otp"));
    } catch (error) {
      console.error(error)
      toast.error(error?.data || "Login failed. Please try again.");
      setErrors({ general: error?.data || "Login failed. Please try again." });
    }
  };
  useEffect(() =>{
    localStorage.clear();
  },[])

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="relative flex justify-center">
          <img src={ICTLogo} alt="ICT Logo" className="w-16 h-16" />
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <div className="text-center mb-4">
            <div className="flex justify-center mb-4">
              <div className="flex items-center space-x-2">
                <img src={EiLogo} alt="EI Logo" className="w-10 h-10" />
                <div className="text-center mb-[-2rem] mt-[-2rem]">
                  <h2 className="text-3xl font-serif text-purple-800 mb-[0rem]">
                    <span className="font-extrabold">work</span>
                    <span className="font-extrabold">force</span>
                    <span className="text-4xl font-extrabold text-blue-600">
                      HR
                    </span>
                  </h2>
                </div>
              </div>
            </div>
            <h2 className="text-2xl font-bold text-[#626C7A] mb-8">
              Hello there! 👋
            </h2>
          </div>
          <div className="space-y-6">
            <div>
              <label
                htmlFor="emailId"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Email ID
              </label>
              <input
                id="emailId"
                type="email"
                value={formData.emailId}
                onChange={(e) => handleInputChange("emailId", e.target.value)}
                placeholder="Enter your email"
                className={`w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                  errors.emailId ? "border-red-300" : "border-gray-300"
                }`}
              />
              {errors.emailId && (
                <p className="mt-1 text-sm text-red-600">{errors.emailId}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="employeeId"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Employee ID
                <Tooltip
                  placement="right"
                  title="If you are logging in as an SuperAdmin, Mention your employee ID as SUPERADMIN"
                >
                  <InfoOutlined className="!w-5 !h-5 text-gray-600 ml-2" />
                </Tooltip>
              </label>
              <input
                id="employeeId"
                type="text"
                value={formData.employeeId}
                onChange={(e) =>
                  handleInputChange("employeeId", e.target.value)
                }
                placeholder="Enter your employee ID"
                className={`w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                  errors.employeeId ? "border-red-300" : "border-gray-300"
                }`}
              />
              {errors.employeeId && (
                <p className="mt-1 text-sm text-red-600">{errors.employeeId}</p>
              )}
            </div>

            {errors.general && (
              <div className="text-sm text-red-600 text-center">
                {errors.general}
              </div>
            )}

            <button
              onClick={handleLogin}
              disabled={isLoading}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium py-2 px-4 rounded-md transition duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              {isLoading ? "Logging in..." : "Log in"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;
